import React, { FC, useEffect, useState } from 'react';
import { FormGroup, FormControlLabel, Checkbox, Box } from '@mui/material';
import { EventStatusDisplayValue, SelectedFiltersType, WebhookEventType } from '../types/types';
import { BaseFiltersPanel, BaseFilterSection } from '../../Paylinks/components/BaseFiltersPanel';
import { DatePicker } from '../../Paylinks/components';

type Props = {
  /** Whether or not to show the filter drawer */
  show: boolean;
  /** Function to call when panel is closed */
  onClosePanel: () => void;
  /** Event types available */
  eventTypes: WebhookEventType[];
  /** Selected filters */
  selectedFilters: SelectedFiltersType;
  /** Test id */
  dataTestId?: string;
  /** Applies selected filters and resets pagination */
  applyEventsFilters: (selectedFilters: SelectedFiltersType) => void;
};

/** Static copy */
const statusText = 'Status';
const eventText = 'Events';

const statusFilterOptions = [
  {
    value: EventStatusDisplayValue.FAILURE,
    text: 'Failure',
  },
  {
    value: EventStatusDisplayValue.PENDING,
    text: 'Pending',
  },
  {
    value: EventStatusDisplayValue.SUCCESS,
    text: 'Success',
  },
];

const FiltersPanel: FC<Props> = (props) => {
  const { show, onClosePanel, eventTypes, selectedFilters, applyEventsFilters, dataTestId } = props;

  const defaultStatusFilters = statusFilterOptions.map((option) =>
    selectedFilters.selectedStatusFilters.has(option.value),
  );
  const defaultEventFilters = eventTypes.map((eventOption) =>
    selectedFilters.selectedEventFilters.has(eventOption.eventName),
  );
  const emptyEventFilters = eventTypes.map(() => false);
  const emptyStatusFilters = statusFilterOptions.map(() => false);
  const [statusFilters, setStatusFilters] = useState(defaultStatusFilters);
  const [eventFilters, setEventFilters] = useState(defaultEventFilters);
  const [applyLoading, setApplyLoading] = useState(false);
  const [initialSendStartDate, setInitialSendStartDate] = useState<Date | null>(
    selectedFilters.selectedInitialSendDateFrom,
  );
  const [initialSendEndDate, setInitialSendEndDate] = useState<Date | null>(
    selectedFilters.selectedInitialSendDateTo,
  );
  const [lastAttemptStartDate, setLastAttemptStartDate] = useState<Date | null>(
    selectedFilters.selectedLastAttemptDateFrom,
  );
  const [lastAttemptEndDate, setLastAttemptEndDate] = useState<Date | null>(
    selectedFilters.selectedLastAttemptDateTo,
  );

  // Calculate the number of active filters
  const filtersCount =
    statusFilters.filter(Boolean).length +
    eventFilters.filter(Boolean).length +
    (initialSendStartDate ? 1 : 0) +
    (initialSendEndDate ? 1 : 0) +
    (lastAttemptStartDate ? 1 : 0) +
    (lastAttemptEndDate ? 1 : 0);

  useEffect(() => {
    setStatusFilters(
      statusFilterOptions.map((option) => selectedFilters.selectedStatusFilters.has(option.value)),
    );
    setEventFilters(
      eventTypes.map((eventOption) =>
        selectedFilters.selectedEventFilters.has(eventOption.eventName),
      ),
    );
    setInitialSendStartDate(selectedFilters.selectedInitialSendDateFrom);
    setInitialSendEndDate(selectedFilters.selectedInitialSendDateTo);
    setLastAttemptStartDate(selectedFilters.selectedLastAttemptDateFrom);
    setLastAttemptEndDate(selectedFilters.selectedLastAttemptDateTo);
  }, [eventTypes, selectedFilters]);

  /** Handles resetting the filters */
  const resetFilters = () => {
    setStatusFilters(defaultStatusFilters);
    setEventFilters(defaultEventFilters);
    setInitialSendStartDate(selectedFilters.selectedInitialSendDateFrom);
    setInitialSendEndDate(selectedFilters.selectedInitialSendDateTo);
    setLastAttemptStartDate(selectedFilters.selectedLastAttemptDateFrom);
    setLastAttemptEndDate(selectedFilters.selectedLastAttemptDateTo);
  };

  /** Handles clearing all filters */
  const clearFilters = () => {
    setStatusFilters(emptyStatusFilters);
    setEventFilters(emptyEventFilters);
    setInitialSendStartDate(null);
    setInitialSendEndDate(null);
    setLastAttemptStartDate(null);
    setLastAttemptEndDate(null);
  };

  /** Handles closing the panel */
  const onClose = () => {
    // Reset state
    resetFilters();
    // Close callback
    onClosePanel();
  };

  /** Handles the onChange for the status filters */
  const onStatusChange = (index: number) => {
    const copyFilters = statusFilters.map((selected, i) => (i === index ? !selected : selected));
    setStatusFilters(copyFilters);
  };

  /** Handles the onChange for the event filters */
  const onEventChange = (index: number) => {
    const copyFilters = eventFilters.map((selected, i) => (i === index ? !selected : selected));
    setEventFilters(copyFilters);
  };

  /** Handles clicking the Apply button */
  const onApply = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    // Calculate the status filters that are selected
    const statusFiltersApplied: Set<string> = new Set();
    statusFilters.forEach((filterValue, index) => {
      if (filterValue) {
        statusFiltersApplied.add(statusFilterOptions[index].value);
      }
    });
    // Calculate the event filters that are selected
    const eventFiltersApplied: Set<string> = new Set();
    eventFilters.forEach((filterValue, index) => {
      if (filterValue) {
        eventFiltersApplied.add(eventTypes[index].eventName);
      }
    });

    const appliedFilters: SelectedFiltersType = {
      selectedStatusFilters: statusFiltersApplied,
      selectedEventFilters: eventFiltersApplied,
      selectedInitialSendDateTo: initialSendEndDate,
      selectedInitialSendDateFrom: initialSendStartDate,
      selectedLastAttemptDateTo: lastAttemptEndDate,
      selectedLastAttemptDateFrom: lastAttemptStartDate,
    };
    try {
      setApplyLoading(true);
      applyEventsFilters(appliedFilters);
    } catch {
      // Reset state in case something went wrong
      resetFilters();
      onClosePanel();
      return;
    } finally {
      setApplyLoading(false);
    }
    // Close panel and reset the button states
    onClosePanel();
  };

  return (
    <BaseFiltersPanel
      isOpen={show}
      onCancel={onClose}
      onReset={clearFilters}
      onSubmit={onApply}
      filtersCount={filtersCount}
      isSubmitting={applyLoading}
    >
      <Box data-testid={dataTestId}>
        {/** Status filters */}
        <BaseFilterSection title={statusText}>
          <FormGroup data-testid={dataTestId ? `${dataTestId}-statusFilters` : undefined}>
            {statusFilterOptions.map((option, index) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    value={option.value}
                    onChange={() => onStatusChange(index)}
                    data-testid={dataTestId ? `${dataTestId}-checkbox${option.value}` : undefined}
                    checked={statusFilters[index]}
                  />
                }
                label={option.text}
              />
            ))}
          </FormGroup>
        </BaseFilterSection>

        <BaseFilterSection
          title="Initial send date"
          tooltip="Filter by when the event was first sent to your endpoint."
          sx={{ display: 'flex', gap: 1 }}
        >
          <DatePicker
            name="initialSendFrom"
            label="From"
            value={initialSendStartDate}
            onChange={(date) => setInitialSendStartDate(date)}
            disablePast={false}
            dateOnly
            shouldDisableDate={(date) => {
              if (initialSendEndDate) {
                return date.isAfter(initialSendEndDate);
              }
              return false;
            }}
            closeOnSelect
          />
          <DatePicker
            name="initialSendTo"
            label="To"
            value={initialSendEndDate}
            onChange={(date) => setInitialSendEndDate(date)}
            disablePast={false}
            dateOnly
            shouldDisableDate={(date) => {
              if (initialSendStartDate) {
                return date.isBefore(initialSendStartDate);
              }
              return false;
            }}
            closeOnSelect
          />
        </BaseFilterSection>

        <BaseFilterSection
          title="Last attempt date"
          tooltip="Filter by when the most recent delivery attempt occurred."
          sx={{ display: 'flex', gap: 1 }}
        >
          <DatePicker
            name="lastAttemptFrom"
            label="From"
            value={lastAttemptStartDate}
            onChange={(date) => setLastAttemptStartDate(date)}
            disablePast={false}
            dateOnly
            shouldDisableDate={(date) => {
              if (lastAttemptEndDate) {
                return date.isAfter(lastAttemptEndDate);
              }
              return false;
            }}
            closeOnSelect
          />
          <DatePicker
            name="lastAttemptTo"
            label="To"
            value={lastAttemptEndDate}
            onChange={(date) => setLastAttemptEndDate(date)}
            disablePast={false}
            dateOnly
            shouldDisableDate={(date) => {
              if (lastAttemptStartDate) {
                return date.isBefore(lastAttemptStartDate);
              }
              return false;
            }}
            closeOnSelect
          />
        </BaseFilterSection>

        {/** Event filters */}
        <BaseFilterSection title={eventText}>
          <FormGroup data-testid={dataTestId ? `${dataTestId}-eventFilters` : undefined}>
            {eventTypes.map((eventType, index) => (
              <FormControlLabel
                key={eventType.eventName}
                control={
                  <Checkbox
                    value={eventType.eventName}
                    checked={eventFilters[index]}
                    onChange={() => onEventChange(index)}
                    data-testid={
                      dataTestId ? `${dataTestId}-checkbox${eventType.eventName}` : undefined
                    }
                  />
                }
                label={eventType.eventDescription}
              />
            ))}
          </FormGroup>
        </BaseFilterSection>
      </Box>
    </BaseFiltersPanel>
  );
};

export default FiltersPanel;
